/**
 * AddNewUserModal Component
 *
 * This component is responsible for rendering a modal form to add a new user.
 * It receives `showNewUserModal`, `handleNewUserModalClose`, and `handleNewUserModalSubmit` as props, which are used to control the modal's visibility and handle form submission.
 *
 * It uses a local state `checkedUserNotification` to control the state of the notification switch in the form.
 * The `handleUserNotificationChange` function is used to update the state of the notification switch.
 *
 * The form in the modal collects various user details like last name, first name, email, phone, user role, location, and notification preference.
 *
 * The modal has two buttons in the footer, one to close the modal and another to submit the form.
 *
 * @component
 */

import React, { useContext, useEffect, useState } from "react";
import UserContext from "../../../../context/UserContext";
import { Button, Form, Modal } from "react-bootstrap";
import "../CompanySettingsPage.scss";
import USER_ROLES from "../../../../constants/userRoles";
import useTranslation from "../../../../hooks/useTranslation";
import TranslatedRoles from "../../../../hooks/useTranslatedRoles";
import useTranslatedLanguages from "../../../../hooks/useTranslatedLanguages";
import { getAllCompanies } from "../../../../api/settingsCompanyApi";

const AddNewUserModal = ({
  showNewUserModal,
  handleNewUserModalClose,
  handleNewUserModalSubmit,
}) => {
  const { user } = useContext(UserContext);
  const { t } = useTranslation("settingsPage");
  const companyId = Number(user.company_id);
  const [companyList, setCompanyList] = useState([]);
  const [formData, setFormData] = useState({
    lastname: "",
    firstname: "",
    email: "",
    phonenumber: "",
    language: "hu",
    timezone_offset: 2,
    roles: "",
    /*     location: [], */
    status: "P",
    company_id: companyId,
    emailnotification: false,
    emailreport: false,
  });
  // Use the useState hook to control the state of the notification switch
  const [checkedUserNotification, setCheckedUserNotification] = useState(false);
  const translatedRoles = TranslatedRoles();

  // Function to fetch companies
  const fetchCompanies = async () => {
    try {
      const response = await getAllCompanies();

      // Handle both response and response.data cases
      const companies = Array.isArray(response) ? response : response.data;

      if (companies) {
        setCompanyList(companies); // Set the company list
      } else {
        console.error(
          "Failed to fetch companies: Response is invalid",
          response
        );
      }
    } catch (error) {
      console.error("Error fetching companies:", error);
    }
  };

  useEffect(() => {
    if (user.roles === USER_ROLES.SUPER_ADMIN) {
      fetchCompanies();
    }
  }, [user.roles]);

  useEffect(() => {
    setFormData((prevFormData) => ({
      ...prevFormData,
      emailnotification: checkedUserNotification,
    }));
  }, [checkedUserNotification]);

  // Define the function to handle the change of the notification switch
  const handleUserNotificationChange = (e) => {
    setCheckedUserNotification(e.target.checked);
  };

  return (
    <>
      <Modal
        show={showNewUserModal}
        onHide={handleNewUserModalClose}
        backdrop="static"
        keyboard={false}
        size="lg"
      >
        <Modal.Header closeButton>
          <Modal.Title>{t("add_new_user")}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            {/* If user is a superadmin, also should be able to select a company as well */}
            {user.roles === USER_ROLES.SUPER_ADMIN && (
              <Form.Group className="mb-3" controlId="newuserform__company">
                <Form.Label>{t("company")}</Form.Label>
                <Form.Select
                  aria-label="Cég"
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      company_id: e.target.value,
                    })
                  }
                >
                  <option value="">{t("selectCompany")}...</option>
                  {companyList.length > 0 ? (
                    companyList.map((company) => (
                      <option key={company.companyid} value={company.companyid}>
                        {company.companyname}
                      </option>
                    ))
                  ) : (
                    <option disabled>{t("noCompaniesAvailable")}</option>
                  )}
                </Form.Select>
              </Form.Group>
            )}
            {/* ----- USER LASTNAME ----- */}
            <Form.Group className="mb-3" controlId="newuserform__lastname">
              <Form.Label>{t("lastname")} *</Form.Label>
              <Form.Control
                type="text"
                placeholder="Cserepes"
                required
                onChange={(e) =>
                  setFormData({ ...formData, lastname: e.target.value })
                }
              />
            </Form.Group>
            {/* ----- USER FIRSTNAME ----- */}
            <Form.Group className="mb-3" controlId="newuserform__firstname">
              <Form.Label>{t("firstname")} *</Form.Label>
              <Form.Control
                type="text"
                placeholder="Virág"
                required
                onChange={(e) =>
                  setFormData({ ...formData, firstname: e.target.value })
                }
              />
            </Form.Group>
            {/* ----- USER EMAIL ----- */}
            <Form.Group className="mb-3" controlId="newuserform__email">
              <Form.Label>{t("email")} *</Form.Label>
              <Form.Control
                type="email"
                placeholder="mintapeter@ceged.hu"
                required
                onChange={(e) =>
                  setFormData({ ...formData, email: e.target.value })
                }
              />
            </Form.Group>

            {/* ----- USER PHONE ----- */}
            <Form.Group className="mb-3" controlId="newuserform__phone">
              <Form.Label>{t("phone")} *</Form.Label>
              <Form.Control
                type="phone"
                placeholder="+36301234567"
                required
                onChange={(e) =>
                  setFormData({ ...formData, phonenumber: e.target.value })
                }
              />
            </Form.Group>
            {/* ----- USER LANGUAGE ----- */}
            <Form.Group className="mb-3" controlId="newuserform__language">
              <Form.Label>{t("language")} *</Form.Label>
              <Form.Select
                aria-label="Nyelv"
                onChange={(e) =>
                  setFormData({ ...formData, language: e.target.value })
                }
              >
                {useTranslatedLanguages().map((language) => (
                  <option key={language.value} value={language.value}>
                    {language.label}
                  </option>
                ))}
              </Form.Select>
            </Form.Group>
            {/* ----- USER ROLE ----- */}
            <Form.Group className="mb-3" controlId="newuserform__userrole">
              <Form.Label>{t("role")} *</Form.Label>
              {/* If the user is superadmin they should see every option, if the user is a companyadmin only see the companyadmin and the companyuser option, the others doesnt see this modal and page at all  */}
              {user.roles === USER_ROLES.SUPER_ADMIN ? (
                <Form.Select
                  aria-label="Szerepkör"
                  onChange={(e) =>
                    setFormData({ ...formData, roles: e.target.value })
                  }
                >
                  <option value="">{t("selectRole")}...</option>
                  {
                    // If the user is superadmin, show all the roles
                    Object.keys(translatedRoles).map((role) => (
                      <option key={role} value={role}>
                        {translatedRoles[role]}
                      </option>
                    ))
                  }
                </Form.Select>
              ) : (
                // If the user is companyadmin, show only the companyadmin and companyuser roles
                <Form.Select
                  aria-label="Szerepkör"
                  onChange={(e) =>
                    setFormData({ ...formData, roles: e.target.value })
                  }
                >
                  <option value="">{t("selectRole")}...</option>
                  <option value={USER_ROLES.COMPANY_ADMIN}>
                    {t("COMPANY_ADMIN")}
                  </option>
                  <option value={USER_ROLES.COMPANYUSER}>
                    {t("COMPANY_USER")}
                  </option>
                </Form.Select>
              )}
            </Form.Group>
            {/* <Form.Group className="mb-3" controlId="newuserform__location ">
              <Form.Select
                aria-label="Telephely"
                multiple={true}
                onChange={(e) =>
                  setFormData({ ...formData, location: e.target.value })
                }
              >
                <option>Válasszon telephelyet...</option>
                <option value="1">Raktár 1</option>
                <option value="2">Raktár 2</option>
                <option value="3">Raktár 3</option>
              </Form.Select>
            </Form.Group> */}
            <Form.Group>
              <Form.Check
                type="switch"
                id="custom-switch"
                label={t("settingsNotifications")}
                checked={checkedUserNotification}
                onChange={handleUserNotificationChange}
              />
            </Form.Group>
            <Form.Group className="mb-3" controlId="newuserform__emailreport">
              <Form.Check
                type="switch"
                id="custom-switch"
                label={t("email_report")}
                checked={formData.emailreport}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    emailreport: e.target.checked,
                  })
                }
              />
            </Form.Group>
          </Form>
          <p className="add-new-user-modal-required-text">
            {t("starSignedFields")}
          </p>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={handleNewUserModalClose}>
            {t("cancel")}
          </Button>
          <Button
            variant="primary"
            onClick={() => handleNewUserModalSubmit(formData)}
          >
            {t("add")}
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default AddNewUserModal;
